import type { PriorVersionSummary } from '@planuze/admin-contracts';
import { VersionDiff } from '@planuze/features-publisher-portal';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  InlineAlert,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@planuze/ui';
import { useState } from 'react';
import { usePackSubmissionsContent } from '../../content/sections/pack-submissions.js';
import { useCompareSubmissionVersions } from '../../hooks/use-pack-submissions.js';

/* Diff de manifest entre 2 versões publicadas do pack da submissão (ADR-0473). Reusa o componente
   VersionDiff (features-publisher-portal, data-driven) + o endpoint interno HMAC do registry via
   admin-service. */

export function VersionCompareCard({
  submissionId,
  currentVersion,
  previousVersions,
}: {
  readonly submissionId: string;
  readonly currentVersion: string;
  readonly previousVersions: readonly PriorVersionSummary[];
}) {
  const copy = usePackSubmissionsContent();
  const c = copy.versionCompareCard;
  /* Opções = versão atual + anteriores, deduplicadas, mantendo a ordem (atual primeiro). */
  const options = [currentVersion, ...previousVersions.map((v) => v.version)].filter(
    (v, i, arr) => arr.indexOf(v) === i,
  );
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const compare = useCompareSubmissionVersions(submissionId, from, to);

  if (options.length < 2) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{c.cardTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{c.needTwoVersions}</p>
        </CardContent>
      </Card>
    );
  }

  const sameSelected = from !== null && to !== null && from === to;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{c.cardTitle}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="compare-from">{c.fromLabel}</Label>
            <Select {...(from !== null && { value: from })} onValueChange={setFrom}>
              <SelectTrigger id="compare-from" className="w-40" aria-label={c.fromAriaLabel}>
                <SelectValue placeholder={c.selectPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {options.map((v) => (
                  <SelectItem key={v} value={v}>
                    v{v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="compare-to">{c.toLabel}</Label>
            <Select {...(to !== null && { value: to })} onValueChange={setTo}>
              <SelectTrigger id="compare-to" className="w-40" aria-label={c.toAriaLabel}>
                <SelectValue placeholder={c.selectPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {options.map((v) => (
                  <SelectItem key={v} value={v}>
                    v{v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {from === null || to === null ? (
          <p className="text-sm text-muted-foreground">{c.selectTwoVersionsHint}</p>
        ) : sameSelected ? (
          <p className="text-sm text-muted-foreground">{c.sameVersionHint}</p>
        ) : compare.isError ? (
          <InlineAlert variant="destructive">{c.compareError}</InlineAlert>
        ) : compare.isLoading || compare.data === undefined ? (
          <div className="grid place-items-center p-8">
            <Spinner size="lg" />
          </div>
        ) : (
          <VersionDiff diff={compare.data} labels={c.labels} />
        )}
      </CardContent>
    </Card>
  );
}
